import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useGallery,
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useSiteCtaStyles } from '../hooks/useSiteCtaStyles';

// ============================================================================
// Smart photo selection
// ============================================================================
// The two Property Overview cards need to feel like a *hero* pair — a curated
// showcase of the property's finishes, layouts, and overall quality. A naive
// `photo_gallery[0]` / `photo_gallery[1]` binding fails whenever a gallery is
// led by a logo or a utility shot, which is why the cards render blank on
// many properties today. We rank the whole gallery instead: reward interior
// showcase keywords, penalize anything that flat-out shouldn't be in a hero
// slot (logos, bathrooms, laundry, parking, utility, blurry). The generator
// still runs on top of this to freeze a curated pick when it has been.

const INTERIOR_KEYWORDS = [
  'interior', 'apartment', 'unit', 'suite', 'residence', 'home',
  'living', 'lounge', 'kitchen', 'bedroom', 'dining', 'great room',
  'den', 'study', 'balcony', 'terrace', 'view',
  'modern', 'luxury', 'finish', 'countertop', 'island', 'cabinetry',
  'hardwood', 'floor-to-ceiling', 'natural light',
];

const AVOID_KEYWORDS = [
  'logo', 'wordmark', 'brand', 'icon',
  'bathroom', 'restroom', 'toilet', 'shower', 'vanity',
  'laundry', 'washer', 'dryer',
  'parking', 'garage', 'carport',
  'utility', 'mechanical', 'storage',
  'trash', 'dumpster', 'maintenance',
  'blur', 'blurry', 'lowres', 'low-res', 'placeholder',
  'floorplan', 'floor-plan', 'diagram', 'sitemap',
];

interface GalleryPhoto {
  image?: string | null;
  alt_text?: string | null;
  title?: string | null;
  description?: string | null;
  source_page_urls?: string | null;
  original_url?: string | null;
}

function photoText(p: GalleryPhoto): string {
  return [p.title, p.description, p.alt_text, p.image, p.original_url, p.source_page_urls]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function scorePhoto(text: string): number {
  if (!text) return 0;
  const positive = INTERIOR_KEYWORDS.reduce(
    (s, kw) => s + (text.includes(kw) ? 1 : 0),
    0,
  );
  // Weight avoid-terms higher so a single "bathroom" hit outranks any
  // incidental positive matches on the same photo.
  const negative = AVOID_KEYWORDS.reduce(
    (s, kw) => s + (text.includes(kw) ? 3 : 0),
    0,
  );
  return positive - negative;
}

/**
 * Rank the property's photo gallery for interior "hero" appeal and return
 * indices for the primary + secondary Property Overview cards. Falls back to
 * `[0, 1]` (or whatever's available) when the gallery lacks any usable text
 * to rank on.
 */
function pickOverviewPhotoIndices(gallery: GalleryPhoto[]): [number, number] {
  if (!gallery.length) return [0, 1];

  const scored = gallery
    .map((photo, index) => ({
      index,
      hasImage: Boolean(photo.image),
      score: scorePhoto(photoText(photo)),
    }))
    .filter((entry) => entry.hasImage);

  if (!scored.length) {
    return [0, Math.min(1, gallery.length - 1)];
  }

  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.index - b.index;
  });

  const primary = scored[0].index;
  const secondary =
    scored.find((entry) => entry.index !== primary)?.index ??
    (primary === 0 ? Math.min(1, gallery.length - 1) : 0);

  return [primary, secondary];
}

// ============================================================================
// Slot group definitions
// ============================================================================
// The static definitions below feed the exported `contentSchema` (which the
// schema linter reads and which powers the editor's field list). At render
// time we build **runtime** definitions with `photo_gallery[<smart>].image`
// so `useSlotGroup` resolves via the ranked pick instead of the raw first
// two indices — user overrides still take priority, and the generator on
// the static def still applies when a generation run has been performed.

const primaryCtaDef: SlotGroupDefinition = {
  label: 'Primary CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Label', type: 'text', defaultValue: 'Explore Floor Plans' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const secondaryCtaDef: SlotGroupDefinition = {
  label: 'Secondary CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/photos' },
    label: { label: 'Label', type: 'text', defaultValue: 'View Gallery' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const primaryImageDef: SlotGroupDefinition = {
  label: 'Primary Photo',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick a standout interior photo of the property that showcases its finishes, layouts, or overall quality — a living room, kitchen, bedroom, or architectural detail that makes the strongest first impression. Do not pick a logo, wordmark, floor-plan diagram, bathroom, laundry room, parking lot, utility or storage space, or any blurry / low-quality photo.',
    context:
      'One of two overlapping hero photos in the Property Overview section — this is the landscape-oriented image on the left, layered behind the secondary. The other card must be a different standout interior. Alt text should describe the actual room shown.',
  },
};

const secondaryImageDef: SlotGroupDefinition = {
  label: 'Secondary Photo',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick a second standout interior photo of the property that shows a different room or angle than the primary — another strong showcase of finishes, layout, or quality. Do not pick a logo, wordmark, floor-plan diagram, bathroom, laundry room, parking lot, utility or storage space, or any blurry / low-quality photo.',
    context:
      'One of two overlapping hero photos in the Property Overview section — this is the landscape-oriented image on the right, layered in front of the primary. Both cards are the same size. Must be a different interior than the primary card. Alt text should describe the actual room shown.',
  },
};

/**
 * Clone a static image slot def but swap its `defaultField` / `sourceBinding`
 * to point at the runtime-picked gallery index. Preserves the label,
 * generator, and every other schema-facing property of the source def.
 */
function makeImageSlotDef(
  base: SlotGroupDefinition,
  index: number,
): SlotGroupDefinition {
  return {
    ...base,
    fields: {
      image: {
        label: 'Image',
        type: 'image',
        defaultField: `photo_gallery[${index}].image`,
      },
      alt: {
        label: 'Alt Text',
        type: 'text',
        defaultField: `photo_gallery[${index}].alt_text`,
      },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: index,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };
}

export function PropertyOverview() {
  const gallery = useGallery() as GalleryPhoto[];
  const [primaryIndex, secondaryIndex] = useMemo(
    () => pickOverviewPhotoIndices(gallery),
    [gallery],
  );

  const primaryImageRuntimeDef = useMemo(
    () => makeImageSlotDef(primaryImageDef, primaryIndex),
    [primaryIndex],
  );
  const secondaryImageRuntimeDef = useMemo(
    () => makeImageSlotDef(secondaryImageDef, secondaryIndex),
    [secondaryIndex],
  );

  const primaryCta = useSlotGroup('propertyoverview.cta_link', primaryCtaDef);
  const secondaryCta = useSlotGroup('propertyoverview.cta_secondary', secondaryCtaDef);
  const primaryImage = useSlotGroup('propertyoverview.image_primary', primaryImageRuntimeDef);
  const secondaryImage = useSlotGroup('propertyoverview.image_secondary', secondaryImageRuntimeDef);

  useRevealStyles();
  useSiteCtaStyles();
  const blurbRef = useScrollReveal<HTMLDivElement>();
  const mediaRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="po-section">
      <div className="po-layout">
        <div className="po-text-column reveal-fade-up">
          <div ref={blurbRef} className="po-text-inner">
            <RichTextContent
              path="propertyoverview.title"
              as="h2"
              className="po-title font-heading"
            />

            <RichTextContent
              path="propertyoverview.blurb"
              as="div"
              className="po-blurb font-body"
            />

            <div className="po-cta-row">
              <EditableSlotGroup groupPath="propertyoverview.cta_link" definition={primaryCtaDef}>
                <a
                  href={primaryCta.url || '/floor-plans'}
                  target={primaryCta._target || '_self'}
                  className="site-cta site-cta--primary"
                >
                  {primaryCta.label || 'Explore Floor Plans'}
                </a>
              </EditableSlotGroup>

              <EditableSlotGroup groupPath="propertyoverview.cta_secondary" definition={secondaryCtaDef}>
                <a
                  href={secondaryCta.url || '/photos'}
                  target={secondaryCta._target || '_self'}
                  className="site-cta site-cta--secondary"
                >
                  {secondaryCta.label || 'View Gallery'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>
        </div>

        <div ref={mediaRef} className="po-media-column reveal-fade-up">
          <div className="po-media">
            <EditableSlotGroup
              groupPath="propertyoverview.image_primary"
              definition={primaryImageRuntimeDef}
              className="po-media-item po-media-item--primary"
            >
              {primaryImage.image ? (
                <VibesitesImage
                  url={primaryImage.image}
                  alt={primaryImage.alt || ''}
                  className="po-media-img"
                  sizes="(min-width: 992px) 34vw, 64vw"
                />
              ) : (
                <div className="po-media-img po-media-img--empty" aria-hidden="true" />
              )}
            </EditableSlotGroup>

            <EditableSlotGroup
              groupPath="propertyoverview.image_secondary"
              definition={secondaryImageRuntimeDef}
              className="po-media-item po-media-item--secondary"
            >
              {secondaryImage.image ? (
                <VibesitesImage
                  url={secondaryImage.image}
                  alt={secondaryImage.alt || ''}
                  className="po-media-img"
                  sizes="(min-width: 992px) 34vw, 64vw"
                />
              ) : (
                <div className="po-media-img po-media-img--empty" aria-hidden="true" />
              )}
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        .po-section {
          position: relative;
          isolation: isolate;
          background: var(--vibe-color-background);
        }

        .po-layout {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
          max-width: 84rem;
          margin: 0 auto;
          padding: 3.5rem 1.5rem;
          align-items: center;
        }

        .po-text-inner {
          max-width: 40rem;
        }

        .po-title {
          margin: 0 0 1.75rem;
          /* Standardized Home section-title size (see FloorplansList,
             AmenitiesForYou, NeighborhoodSection, ResidentReview,
             SocialFeedSection). Kept at font-weight 700 to match. */
          font-size: clamp(2.25rem, 4vw, 3rem);
          line-height: 1.05;
          font-weight: 700;
          color: var(--vibe-color-primary);
          text-transform: none;
        }

        .po-title p,
        .po-title span {
          margin: 0;
          color: inherit;
          font: inherit;
        }

        .po-blurb {
          margin: 0 0 2rem;
          font-size: 1.0625rem;
          line-height: 1.7;
          color: var(--vibe-color-text);
        }

        .po-blurb p {
          margin: 0 0 1.15rem;
        }

        .po-blurb p:last-child {
          margin-bottom: 0;
        }

        .po-cta-row {
          display: grid;
          grid-auto-flow: row;
          grid-template-columns: max-content;
          gap: 1rem;
        }

        .po-cta-row .site-cta {
          width: 100%;
        }

        .po-media-column {
          min-width: 0;
        }

        .po-media {
          position: relative;
          width: 100%;
          aspect-ratio: 4 / 3;
        }

        .po-media-item {
          position: absolute;
          overflow: hidden;
          box-shadow: 0 22px 55px rgba(0, 0, 0, 0.18);
        }

        .po-media-item--primary {
          top: 0;
          left: 0;
          width: 68%;
          aspect-ratio: 4 / 3;
          z-index: 1;
        }

        .po-media-item--secondary {
          right: 0;
          bottom: 0;
          width: 68%;
          aspect-ratio: 4 / 3;
          z-index: 2;
        }

        .po-media-img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .po-media-img--empty {
          background: var(--vibe-color-secondary);
        }

        /* Mobile: center the copy column and CTA stack. Desktop keeps the
           left-aligned two-column layout via the min-width rules below. */
        @media (max-width: 767.98px) {
          .po-text-column {
            text-align: center;
          }
          .po-text-inner {
            margin-left: auto;
            margin-right: auto;
          }
          .po-cta-row {
            justify-content: center;
          }
        }

        @media (min-width: 768px) {
          .po-layout {
            grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr);
            gap: 2rem;
            padding: 5rem 2rem;
          }
        }

        @media (min-width: 992px) {
          .po-layout {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            gap: 4rem;
            padding: 6rem 2.5rem;
          }

          .po-media {
            aspect-ratio: 4 / 3.15;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.title': {
      label: 'Title',
      type: 'richtext',
      defaultField: 'property_home_romance_title',
      defaultValue: '<p>For the next generation of innovators</p>',
    },
    'propertyoverview.blurb': {
      label: 'Blurb',
      type: 'richtext',
      defaultField: 'property_long_description',
      defaultValue:
        '<p>Discover the perfect balance of academic focus and social energy. Designed with students in mind, our fully furnished floor plans offer high-speed internet, in-unit laundry, and modern kitchens — everything you need to stay connected and comfortable.</p><p>Ready to elevate your college experience? Explore our floor plans and check out layouts designed to fit your lifestyle.</p>',
    },
  },
  slotGroups: {
    'propertyoverview.cta_link': primaryCtaDef,
    'propertyoverview.cta_secondary': secondaryCtaDef,
    'propertyoverview.image_primary': primaryImageDef,
    'propertyoverview.image_secondary': secondaryImageDef,
  },
};

(PropertyOverview as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PropertyOverview;
